import React, { useMemo } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { CampaignFeaturesMap } from "./campaignConfig";
import DropDownValue from "../baseComponents/dropdownvalue";
import CampaignMode from "../../interfaces/campaignModes";
import { RootState } from "../../store/store";
import PermissionManagement from "../../permissions/permissionManagement";
import { DOCUMENT_GENERATION, CALL_CAMPAIGN, KNOW_BOT_CAMPAIGN, CRAFT_AI_BOT_CAMPAIGN } from "../../constants/constants";

interface CampaignHeaderProps {
    campaignMode: CampaignMode;
    campaignType: string;
    setCampaignMode: (val: CampaignMode) => void;
    setCampaignType: (type: string) => void;
};

const styles = {
    toggleGroup: {
        px: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        mt: 3,
        justifyContent: 'space-between'
    },
    campaignSelectorRunMode: {
        px: 2,
        mb: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
    },
};

type ToggleButtonOption = {
    value: CampaignMode;
    label: string;
};

const toggleButtons = [
    { value: CampaignMode.RUN, label: "RUN CAMPAIGN" },
    { value: CampaignMode.RESULTS, label: "CAMPAIGN RESULTS" },
    { value: CampaignMode.TEMPLATES, label: "MANAGE TEMPLATES"}
];

const CampaignHeader: React.FC<CampaignHeaderProps> = ({
    campaignMode,
    campaignType,
    setCampaignMode,
    setCampaignType,
}) => {
    const state = useSelector((state: RootState) => state);

    const campaigns = useMemo(() => {
        const allCampaigns = Object.values(CampaignFeaturesMap);
        return allCampaigns.filter((campaign) => {
            if (campaign.key === DOCUMENT_GENERATION) {
                return PermissionManagement.isDocumentGenerationEnabled(state);
            }
            if (campaign.key === CALL_CAMPAIGN) {
                return PermissionManagement.isCallCampaignEnabled(state);
            }
			if (campaign.key === KNOW_BOT_CAMPAIGN) {
				return PermissionManagement.isKnowlVoiceBotCampaignEnabled(state)
			}
			if (campaign.key === CRAFT_AI_BOT_CAMPAIGN) {
				return PermissionManagement.isCraftAiBotCampaignEnabled(state)
			}
            return false;
        });
    }, [state]);

    return (
        <Box>
            <Box sx={styles.toggleGroup}>
                <Typography variant="h6">Campaign</Typography>
                <Box>
                    <ToggleButtonGroup
                        value={campaignMode}
                        exclusive
                        onChange={(_, value: CampaignMode) => setCampaignMode(value)}
                        size="medium"
                        aria-label="view mode"
                    >
                        {toggleButtons.map((val: ToggleButtonOption, index: number) => {
                            return (
                                <ToggleButton key={index} value={val.value} aria-label={val.label}>
                                    <Typography variant="subtitle2">{val.label}</Typography>
                                </ToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>
                </Box>
            </Box>
            <Box sx={styles.campaignSelectorRunMode}>
                <DropDownValue
                    label="Select Campaign"
                    selectedValue={campaignType}
                    onSelection={(v) => setCampaignType(v as string)}
                    items={campaigns}
                    multiple={false} // explicitly single-select
                    style={{ minWidth: 260 }}
                    noOptionsText="No campaigns available"
					keyProp="key"
					valueProp="key"
					displayProp="label"
                />
            </Box>
        </Box>
    );
};

export default CampaignHeader;